import {Plus, Trash2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {MutedText} from '@/components/shared/MutedText'

export interface ResponseFilterRow {
    field: string
    value: string
}

export function ResponseFiltersEditor({rows, onChange}: {
    rows: ResponseFilterRow[]
    onChange: (next: ResponseFilterRow[]) => void
}) {
    const i18n = useI18n()

    const update = (i: number, patch: Partial<ResponseFilterRow>) => {
        onChange(rows.map((r, idx) => idx === i ? {...r, ...patch} : r))
    }

    const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i))

    const add = () => onChange([...rows, {field: '', value: ''}])

    return (
        <>
            <MutedText size="xs" className="block mb-2">
                {i18n.inboundConnectors.responseFiltersHint}
            </MutedText>
            <div className="space-y-2" data-testid="response-filters">
                {rows.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <Input
                            value={f.field}
                            onChange={e => update(i, {field: e.target.value})}
                            placeholder={i18n.inboundConnectors.responseFilterFieldPlaceholder}
                            data-testid={`response-filter-field-${i}`}
                            className="flex-1"
                        />
                        <span className="text-muted-foreground text-sm">=</span>
                        <Input
                            value={f.value}
                            onChange={e => update(i, {value: e.target.value})}
                            placeholder={i18n.inboundConnectors.responseFilterValuePlaceholder}
                            data-testid={`response-filter-value-${i}`}
                            className="flex-1"
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                            onClick={() => remove(i)}
                            data-testid={`response-filter-remove-${i}`}
                            aria-label={i18n.common.remove}
                        >
                            <Trash2 className="h-4 w-4"/>
                        </Button>
                    </div>
                ))}
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={add}
                    data-testid="btn-add-response-filter"
                >
                    <Plus className="h-4 w-4 mr-1"/>
                    {i18n.inboundConnectors.addResponseFilter}
                </Button>
            </div>
        </>
    )
}
